import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { emitBetslipDepositToPlaceRequested, emitBetslipLoginRequested } from "../../contract/betslip-api";
import { getObbBettingState } from "@ppb/tbd-store/state/betting/obb-betting/obb-betting-selectors";
import {
  BettingObbIncrementStakeAction,
  BettingObbPlaceBetsAction,
  BettingDepositToPlaceBetAction,
  BettingObbClearAction,
  BETTING__OBB_PLACE_BETS,
  BETTING__DEPOSIT_TO_PLACE_BET,
  BETTING__OBB_CLEAR_ACTION,
  BETTING__OBB_INCREMENT_STAKE_ACTION,
} from "@ppb/tbd-store/actions/betting";
import { createOddsMovementSelector } from "../../store/state/betslip-module/betslip-odds-movement-selectors";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";
import { AlertType, QuickStakesProps, KeyboardSeparator } from "@ppb/the-wall-common/types";
import type { BetControlsProps } from "../BetControls/snowflakes/BetControls/BetControls.types";
import { BetslipAccordionHeaderClick, UI__BETSLIP_ACCORDION_HEADER_CLICK } from "@ppb/tbd-store/actions/betslip";
import { i18n } from "../../helpers/i18n";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { createGetObbIsDepositRequiredSelector, buildQuickStakesSelector } from "../betslip-mapper";
import { createValidationsSelector } from "../ObbNotifier/obb-notifier-mapper";
import { createGetAvailabilityFailuresOnStakedBetsSelector } from "../connected-obb-betslip-mapper";
import { buildSbkPlaceBetButtonLabels } from "../../helpers/betslip-place-button-helper";
import { buildSbkBalanceAfterBetLabel } from "../../helpers/betslip-balance-helper";
import { hasSameBaseBets, isCombinedPotentialBet, ObbMultipleDetails } from "../../helpers/obb";
import { ObbXOfNLegParams } from "@ppb/tbd-store/state/betting/obb-betting/ObbBetting.types";
import {
  getBetslipPlaceStatus,
  getObbOddsMovement,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import type {
  BetslipLocalization,
  BetslipSession,
  SportsbookBetslipBalance,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
  SportsbookBetslipPreferences,
} from "../../contract";
import { toKeyboardSeparator } from "../../helpers/keyboard-helper";

type PlaceFooterI18n = {
  balanceAfterBet: string;
  totalReturns: string;
  removeLabel: string;
  freeBetsLabel?: string;
  freeBetsAlertRemoveLabel?: string;
  termsLabel?: string;
  termsLinkLabel?: string;
  voidBlurbText: string;
  multiples: string;
  additionalMultiples: string;
  singles: string;
  casts: string;
  oddsLabel: string;
  stakeLabel: string;
  oddsMovementUp: string;
  oddsMovementDown: string;
  eachWay: string;
  eachWaySubtitle: string;
  betBuilder: string;
  multiBetBuilder: string;
  priceBoosts: string;
  tabAllTitle: string;
  tabBetBuildersTitle: string;
  tabMultiplesTitle: string;
  tabCastTitle: string;
  tabSinglesTitle: string;
  tabLotteriesTitle: string;
  betslipAriaTitle: string;
};

export type ContainerProps = {
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  session: BetslipSession;
  capabilities: SportsbookBetslipCapabilities;
  preferences: SportsbookBetslipPreferences;
  balance: SportsbookBetslipBalance;
};

type CardProps = {
  i18n: PlaceFooterI18n;
  isLoggedIn: boolean;
  totalReturns: string;
  isPanelDisabled: boolean;
  isPlaceDisabled: boolean;
  isCollapsed?: boolean;
  hasError: boolean;
  isDepositRequired: boolean;
  placeBtnLabel: string;
  placeBtnSecondaryLabel?: string | string[];
  placeBtnLoadingLabel?: string;
  reversePlaceBtnLabels?: boolean;
  balanceAfterBet?: string;
  currencySymbol: BetControlsProps["currencySymbol"];
  quickStakes: QuickStakesProps["quickStakes"];
  separator: KeyboardSeparator;
  singles: string[];
  multiplesGroups: Array<Array<ObbMultipleDetails>>;
  termsUrl?: string;
};

export type StateProps = CardProps | Record<string, never>;

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const i18nLabels = {
    balanceAfterBet: i18n({ key: "I18N.BETSLIP.BALANCE_AFTER_BET" }),
    totalReturns: i18n({ key: "I18N.BETSLIP.TOTAL_RETURNS" }),
    removeLabel: i18n({ key: "I18N.BETSLIP.REMOVE_ALL" }),
    freeBetsLabel: i18n({ key: "I18N.BETSLIP.USE_FREE_BET_WITHOUT_AMOUNT" }),
    freeBetsAlertRemoveLabel: i18n({ key: "I18N.BETSLIP.REMOVE" }),
    termsLabel: i18n({ key: "I18N.BETSLIP.BETS_ACCEPTED_IN_ACCORDANCE_WITH" }),
    voidBlurbText: i18n({ key: "I18N.BETSLIP.OBB.VOID_BLURB" }),
    multiples: i18n({ key: "I18N.BETSLIP.MULTIPLES" }),
    additionalMultiples: "",
    singles: i18n({ key: "I18N.BETSLIP.SINGLES" }),
    casts: "",
    oddsLabel: i18n({ key: "I18N.BETSLIP.ODDS" }),
    stakeLabel: i18n({ key: "I18N.BETSLIP.STAKE" }),
    oddsMovementUp: "",
    oddsMovementDown: "",
    eachWay: "",
    eachWaySubtitle: "",
    betBuilder: i18n({ key: "I18N.BETSLIP.BET_BUILDER" }),
    multiBetBuilder: "s",
    termsLinkLabel: i18n({ key: "I18N.BETSLIP.TEXT_RULES" }),
    priceBoosts: "",
    totalStake: "",
    placing: "",
    tabAllTitle: i18n({ key: "I18N.BETSLIP.TABS.ALL" }),
    tabBetBuildersTitle: i18n({ key: "I18N.BETSLIP.TABS.BET_BUILDER" }),
    tabMultiplesTitle: i18n({ key: "I18N.BETSLIP.TABS.MULTIPLES" }),
    tabCastTitle: i18n({ key: "I18N.BETSLIP.TABS.CAST_BET" }),
    tabSinglesTitle: i18n({ key: "I18N.BETSLIP.TABS.SINGLES" }),
    tabLotteriesTitle: i18n({ key: "I18N.BETSLIP.TABS.LOTTERIES" }),
    betslipAriaTitle: i18n({ key: "I18N.BETSLIP.TITLE" }),
  };

  const getIsDepositRequired = createGetObbIsDepositRequiredSelector();
  const getOddsMovementState = createOddsMovementSelector();
  const getValidations = createValidationsSelector();
  const getAvailabilityFailuresOnStakedBets = createGetAvailabilityFailuresOnStakedBetsSelector();
  const getQuickStakes = buildQuickStakesSelector();

  return (
    appState: BetslipApplicationState,
    { localization, config, session, capabilities, preferences, balance }: ContainerProps,
  ): StateProps => {
    const { currencySymbol } = localization;
    if (!getIsBetslipLoaded(appState)) {
      return {};
    }

    const termsUrl = config.termsUrls.general;

    const { loggedIn, isAuthenticating } = session;
    const isDepositRequired = getIsDepositRequired(appState, balance);
    const bettingState = getObbBettingState(appState);
    const oddsMovementMap = getOddsMovementState(getObbOddsMovement(appState));
    const hasAnyOddChanged = Object.values(oddsMovementMap).some((potentialBet) => potentialBet.movement !== null);
    const validations = getValidations(appState, {
      localization,
      maxPayoutTermsUrl: config.termsUrls.maxPayout,
      dailyPayoutLimit: capabilities.dailyPayoutLimit,
    });

    const hasStakedPotentialBets = Object.values(bettingState.potentialBets).some((bet) => !!bet.stake);

    const hasAvailabilityFailuresOnStakedBets = getAvailabilityFailuresOnStakedBets(appState);

    const hasError = !!bettingState.failures.betslip;
    const hasBlockingValidations = validations.some((validation) => validation.notification?.type === AlertType.Error);

    const isPlacing = getBetslipPlaceStatus(appState) === "INPROGRESS";
    const isPlaceDisabled = !hasStakedPotentialBets || hasBlockingValidations || hasAvailabilityFailuresOnStakedBets;

    const singles: string[] = [];
    const multiplesGroups: ObbMultipleDetails[][] = [];

    Object.values(bettingState.potentialBets).forEach(({ id, legs }) => {
      const potentialBetLegs = legs.map((legId) => bettingState.legs[legId]);

      if (!isCombinedPotentialBet(potentialBetLegs)) {
        singles.push(id);
        return;
      }

      const params = bettingState.legs[legs[0]].params as ObbXOfNLegParams;

      const matchingGroup = multiplesGroups.find((group) => {
        const groupLegs = bettingState.potentialBets[group[0].id].legs.map((legId) => bettingState.legs[legId]);
        return hasSameBaseBets(potentialBetLegs, groupLegs);
      });

      matchingGroup ? matchingGroup.push({ id, x: params.x }) : multiplesGroups.push([{ id, x: params.x }]);
    });

    const sortedMultiplesGroups = multiplesGroups.map((group) => {
      return group.sort((a, b) => a.x - b.x);
    });

    const filteredMultiplesGroups = sortedMultiplesGroups.map((group) => {
      const hasMissingQuotes = group.some(({ id: potentialBetId }) => {
        return !bettingState.potentialBets[potentialBetId].quote;
      });

      return hasMissingQuotes ? [group[group.length - 1]] : group;
    });

    const totalStake = currencyFormatWithDecimalPlaces({
      ...localization,
      value: bettingState.totalStake ?? 0,
      decimalPlaces: 2,
    });

    const oddsMovement = preferences.acceptOddsMovement;

    const { label, secondaryLabel, loadingLabel, reverseLabels } = buildSbkPlaceBetButtonLabels({
      isPlacing,
      isAuthenticating,
      isDepositRequired,
      isLoggedIn: loggedIn,
      hasStake: !!bettingState.totalStake,
      hasOddsChanged: hasAnyOddChanged,
      interpolatedValues: { stake: totalStake },
      shouldAcceptOddsMovement: oddsMovement,
    });

    const totalReturns = currencyFormatWithDecimalPlaces({
      ...localization,
      value: bettingState.totalPotentialReturns ?? 0,
      decimalPlaces: 2,
    });

    const accountBalance = balance.available ?? 0;

    const balanceAfterBet = buildSbkBalanceAfterBetLabel(localization, {
      accountBalance,
      totalStake: bettingState.totalStake ?? 0,
      isLoggedIn: loggedIn,
    });

    if (!getIsBetslipLoaded(appState)) {
      return {};
    }
    const isCollapsed = appState.betslipModule?.isCollapsed ?? false;

    const quickStakes = getQuickStakes(preferences.quickStakes, localization);
    return {
      i18n: i18nLabels,
      placeBtnLabel: label,
      placeBtnSecondaryLabel: secondaryLabel,
      placeBtnLoadingLabel: loadingLabel,
      reversePlaceBtnLabels: reverseLabels,
      isLoggedIn: loggedIn,
      isDepositRequired,
      hasError,
      isPanelDisabled: isPlacing || !!isAuthenticating,
      isPlaceDisabled,
      isCollapsed,
      totalReturns,
      balanceAfterBet,
      currencySymbol,
      quickStakes,
      separator: toKeyboardSeparator(localization.decimalSeparator),
      singles,
      multiplesGroups: filteredMultiplesGroups,
      termsUrl,
    };
  };
};

export type DispatchActions =
  | BettingDepositToPlaceBetAction
  | BettingObbClearAction
  | BettingObbPlaceBetsAction
  | BettingObbIncrementStakeAction
  | BetslipAccordionHeaderClick
  | BettingObbPlaceBetsAction;

export type DispatchProps = {
  dispatchOnRemoveAllClick: () => void;
  dispatchOnPlaceBetsClick: () => void;
  requestLogin: () => void;
  dispatchDepositRedirect: () => void;
  dispatchAccordionToggle: (isExpanded: boolean) => void;
  dispatchIncrementPress: ({
    potentialBetId,
    increment,
    currencySymbol,
  }: {
    potentialBetId: string;
    increment: number;
    currencySymbol?: string;
  }) => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<DispatchActions>,
) => ({
  dispatchDepositRedirect: () => {
    dispatch<BettingDepositToPlaceBetAction>({ type: BETTING__DEPOSIT_TO_PLACE_BET });
    emitBetslipDepositToPlaceRequested({});
  },
  dispatchOnRemoveAllClick: () => {
    dispatch<BettingObbClearAction>({
      type: BETTING__OBB_CLEAR_ACTION,
    });
  },
  dispatchOnPlaceBetsClick: () => {
    dispatch<BettingObbPlaceBetsAction>({
      type: BETTING__OBB_PLACE_BETS,
    });
  },
  dispatchAccordionToggle: (isExpanded: boolean) => {
    dispatch<BetslipAccordionHeaderClick>({
      type: UI__BETSLIP_ACCORDION_HEADER_CLICK,
      payload: { isExpanded },
    });
  },
  requestLogin: () => {
    emitBetslipLoginRequested();
  },
  dispatchIncrementPress: ({ potentialBetId, increment }) => {
    dispatch<BettingObbIncrementStakeAction>({
      type: BETTING__OBB_INCREMENT_STAKE_ACTION,
      payload: { potentialBetId, increment },
    });
  },
});
